import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAccount, useWalletClient } from 'wagmi'
import {
  ClaimPlatform,
  completeClaimChallenge,
  SignedBlockchainClaimChallenge,
} from '@verisart/nft/src/http'
import {
  ConnectWalletError,
  isConnectWalletError,
  translations,
  useConnectWallet,
  useLinkWallet,
} from '@verisart/nft/src'
import { useAPI } from './api'
import { lightTheme, RainbowKitProvider } from '@rainbow-me/rainbowkit'
import { cssStringFromTheme } from '@rainbow-me/rainbowkit'

interface WalletContextType {
  claimWallet: (
    signedChallenge: SignedBlockchainClaimChallenge
  ) => Promise<void>
  error?: ConnectWalletError | string
  isClaiming: boolean
  isClaimed: boolean
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<ConnectWalletError | string | undefined>(
    undefined
  )
  const [isClaiming, setIsClaiming] = useState(false)
  const [isClaimed, setIsClaimed] = useState(false)
  const { initialized, shopDomain, customer } = useAPI()
  const account = useAccount()
  const { data: walletClient } = useWalletClient()
  const { disconnect } = useConnectWallet()
  const { linkWallet, isLinked } = useLinkWallet(customer?.axios, setError)

  useEffect(() => {
    if (initialized && shopDomain && account.address && walletClient) {
      linkWallet(ClaimPlatform.CUSTOMER_DASHBOARD, walletClient, shopDomain)
    } else if (!account.address) {
      setError(undefined)
    }
  }, [initialized, account, shopDomain, linkWallet, walletClient])

  useEffect(() => {
    if (isLinked) {
      setIsClaimed(true)
    }
  }, [isLinked])

  useEffect(() => {
    if (!account.address) {
      setError(undefined)
    }
  }, [account])

  const theme = useMemo(() => {
    const host = document.getElementById('verisart-app')
    const fonts =
      host?.computedStyleMap().get('font-family')?.toString() ?? 'inherit'

    const theme = {
      ...lightTheme({
        accentColor: '#000',
        borderRadius: 'none',
      }),
      fonts: {
        body: fonts,
      },
    }

    return theme
  }, [])

  // Hack to fix rainbowkit styles not being applied
  useEffect(() => {
    const setup = async () => {
      const css = await import('@rainbow-me/rainbowkit/styles.css')

      const style = document.createElement('style')
      style.innerHTML = `[data-rk] {${cssStringFromTheme(theme)}} ${
        css.default
      }`
      document.head.appendChild(style)

      return () => {
        document.head.removeChild(style)
      }
    }
    setup()
  }, [theme])

  const claimWallet = useCallback(
    async (signedChallenge: SignedBlockchainClaimChallenge) => {
      if (!customer?.axios) {
        return
      }

      setIsClaiming(true)

      try {
        await completeClaimChallenge(customer.axios, {
          accountCommitment: signedChallenge.accountCommitment,
          address: signedChallenge.address,
          challenge: signedChallenge.challenge,
          signature: signedChallenge.signature,
          moveExisting: true,
        })

        setError(undefined)
        setIsClaimed(true)
      } catch (e: any) {
        if (e.cause !== 'already_linked') {
          console.error('Error in connectWallet', e)
          if (isConnectWalletError(e)) {
            setError(
              e.cause === 'already_claimed_by_other'
                ? e
                : 'Sorry, your wallet could not be linked. Please try again.'
            )
          } else {
            await disconnect()
            setError(translations['unknown'] + e.message)
          }
          throw e
        } else {
          setIsClaimed(true)
          setError(undefined)
        }
      } finally {
        setIsClaiming(false)
      }
    },
    [customer, disconnect]
  )

  return (
    <WalletContext.Provider
      value={{ claimWallet, error, isClaiming, isClaimed }}
    >
      <RainbowKitProvider theme={theme} modalSize="compact" appInfo={{}}>
        {children}
      </RainbowKitProvider>
    </WalletContext.Provider>
  )
}

export function useWallet() {
  const context = useContext(WalletContext)
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider')
  }
  return context
}
